/**
 * Datos del DNI para PRE-COMPLETAR el formulario del autor.
 *
 * Es la otra cara de dniMatch: allá el documento CONFIRMA lo que la persona
 * cargó; acá lo completa cuando no hay nada cargado. Y como completar no tiene
 * con qué compararse, lo que importa es distinguir de dónde sale cada dato:
 *
 *  - Del MRZ (apellido, nombres, número, fecha de nacimiento, sexo): tiene
 *    dígitos verificadores, así que la lectura está probada. Van en
 *    `verificados`.
 *  - Del texto suelto del dorso (CUIL, domicilio): no hay nada que los
 *    verifique. El CUIL al menos se contrasta contra el número del MRZ —un
 *    CUIL mal leído en el campo fiscal es peor que ninguno—. El domicilio se
 *    entrega como TEXTO, y aparte repartido en los campos del formulario
 *    (resolverDomicilio), pero solo hasta donde el catálogo de países y
 *    provincias lo respalde: lo que no sea una fila de esas tablas no se
 *    inventa. Y ni así se escribe solo — el domicilio del DNI suele estar
 *    desactualizado, así que lo mira la persona antes de confirmar.
 *
 * Nada de esto se escribe solo: la persona ve lo extraído y confirma.
 *
 * ESPEJO byte-idéntico en jupiter-client/src/common/pages/dniUpload/dniExtract.ts
 */

import type { MrzTd1Data } from "./mrzTd1";
import { cuitParaDni, esCuitValido } from "./cuit";

export interface DatosExtraidos {
  lastName?: string;
  firstName?: string;
  dateOfBirth?: string;
  gender?: "male" | "female" | "nonBinary";
  taxIdType?: "cuitCuil" | "du";
  taxId?: string;
  /** Domicilio tal como se leyó, para que la persona lo acomode a los campos. */
  domicilioLeido?: string;
  /** Los campos que vienen del MRZ, es decir con la lectura probada. */
  verificados: string[];
  /**
   * De dónde salió cada campo. Sin esto el ensamblado es a ciegas: no se puede
   * decidir si un dato nuevo debe pisar a uno viejo, ni explicarle a la persona
   * por qué dice lo que dice.
   *
   *   mrz    — banda del dorso, con dígitos de control. Es una prueba.
   *   dorso  — texto impreso del dorso (CUIL, domicilio). Lectura única.
   *   frente — texto impreso del frente, por geometría. Lectura única.
   */
  origenes?: Record<string, OrigenDeCampo>;
}

export type OrigenDeCampo = "mrz" | "dorso" | "frente";

/**
 * Ensambla dos lecturas en una, SIN perder nada.
 *
 * El bug que esto corrige: el dorso no ensamblaba, reemplazaba. Se cargaba el
 * frente, salían apellido y nombres; se cargaba el dorso —más nítido todavía— y
 * desaparecían, porque el dorso arrancaba poniendo los datos en null y después
 * los pisaba enteros con lo suyo. Si su MRZ no parseaba, lo único que quedaba
 * era el domicilio. Sumar fuentes tiene que aumentar la información, nunca
 * destruirla.
 *
 * La regla de precedencia es una sola y es la del diseño: una lectura PROBADA
 * —la del MRZ, que tiene dígitos de control— pisa a una no probada. Entre dos no
 * probadas gana la que ya estaba, para que recargar una cara no borre la otra.
 */
export function ensamblar(previo: DatosExtraidos | null, nuevo: DatosExtraidos): DatosExtraidos {
  if (!previo) return nuevo;

  const campos: (keyof DatosExtraidos)[] = [
    "lastName", "firstName", "dateOfBirth", "gender", "taxIdType", "taxId", "domicilioLeido",
  ];
  const salida: DatosExtraidos = {
    verificados: [...previo.verificados],
    origenes: { ...(previo.origenes ?? {}) },
  };

  for (const campo of campos) {
    const valorPrevio = previo[campo];
    const valorNuevo = nuevo[campo];
    const nuevoEsProbado = nuevo.verificados.includes(campo as string);
    const usarNuevo = valorNuevo != null && valorNuevo !== "" && (valorPrevio == null || valorPrevio === "" || nuevoEsProbado);

    const elegido = usarNuevo ? valorNuevo : valorPrevio;
    if (elegido != null && elegido !== "") {
      Object.assign(salida, { [campo]: elegido });
      const origen = usarNuevo ? nuevo.origenes?.[campo as string] : previo.origenes?.[campo as string];
      if (origen) salida.origenes![campo as string] = origen;
      if (usarNuevo) {
        if (nuevoEsProbado && !salida.verificados.includes(campo as string)) {
          salida.verificados.push(campo as string);
        } else if (!nuevoEsProbado) {
          salida.verificados = salida.verificados.filter((c) => c !== campo);
        }
      }
    }
  }

  // El taxIdType acompaña al taxId: si uno viene del otro lado, el otro también.
  if (salida.taxId && nuevo.taxId === salida.taxId && nuevo.taxIdType) {
    salida.taxIdType = nuevo.taxIdType;
  }
  return salida;
}

/**
 * CUIL impreso en el dorso. Si se pasa el documento, tiene que coincidir.
 *
 * Y tiene que cerrar el verificador. El tramo del medio puede coincidir con el
 * documento y el número igual no existir —el OCR lee mal el último dígito—, y
 * sin el módulo 11 eso entraba derecho al campo fiscal del autor. Un CUIL mal
 * leído es peor que ninguno.
 */
export function cuilDelDorso(textoDorso: string, documento?: string): string | null {
  const m = String(textoDorso ?? "").match(/CUI[LT]\s*:?\s*([\d\s.-]{11,17})/i);
  if (!m) return null;
  const digitos = m[1].replace(/\D/g, "");
  if (digitos.length !== 11) return null;
  if (!esCuitValido(digitos)) return null;
  if (documento) {
    const esperado = documento.replace(/\D/g, "").replace(/^0+/, "");
    const enCuil = digitos.slice(2, 10).replace(/^0+/, "");
    if (esperado && enCuil !== esperado) return null;
  }
  return `${digitos.slice(0, 2)}-${digitos.slice(2, 10)}-${digitos.slice(10)}`;
}

/** Palabras de dos letras o menos que SÍ forman parte de un nombre de lugar. */
const PARTICULAS = new Set(["DE", "DEL", "LA", "EL", "Y"]);

/**
 * ¿Este pedazo es el rótulo "DOMICILIO", tal como lo pudo leer el OCR?
 *
 * El rótulo está impreso sobre el fondo de seguridad y es de lo peor que se
 * lee. Medido sobre fotos reales salieron "DOMICILIO", "somiciLio",
 * "nomicilio", "ooMGILI0" y hasta "omic" a secas: pedir la palabra entera, o
 * incluso su parte del medio, deja afuera la mitad de las fotos buenas.
 *
 * Lo que sí se sostiene en todas es el arranque: alguna letra redonda mal
 * leída, después una M, y enseguida una C o una G (la C de "MICILIO" también
 * se lee G). Eso es poco, así que el rótulo se busca SOLO entre las primeras
 * palabras de cada línea, que es donde el documento lo imprime.
 */
function pareceRotuloDomicilio(token: string): boolean {
  const t = token.toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (t.length < 3 || t.length > 12) return false;
  if (/MICIL[IL1]O/.test(t)) return true;
  // La M puede venir precedida por cualquier cosa: se midió "somiciLio",
  // "nomicilio", "ooMGILI0", "omic" y "comico". Lo que se sostiene en todas es
  // la M seguida de cerca por una C o una G. Pedir además qué letras la
  // preceden fue el error: dejaba afuera "comico", con el domicilio leído
  // entero dos palabras más allá.
  return /^.{0,3}M[I1L]{0,2}[CG]/.test(t);
}

/**
 * Domicilio del dorso: arranca en su rótulo y sigue hasta el rótulo siguiente
 * (el lugar de nacimiento, el CUIL o la firma), porque ocupa varias líneas.
 * Después se quita la basura que el OCR intercala, que se reconoce porque son
 * fragmentos cortos sin vocales o con símbolos.
 */
export function domicilioDelDorso(textoDorso: string): string | null {
  const lineas = String(textoDorso ?? "").split(/\r?\n/);
  let inicio = -1;
  let primeraLinea = "";
  for (let i = 0; i < lineas.length; i++) {
    const tokens = lineas[i].split(/\s+/).filter(Boolean);
    // El rótulo entero puede venir precedido de basura; el mutilado, no: como
    // ancla es mucho más débil, y aceptarlo en cualquier posición convertiría
    // cualquier palabra con M y C en el comienzo de un domicilio.
    const firme = tokens.slice(0, 2).findIndex((t) =>
      /MICIL[IL1]O/.test(t.toUpperCase().replace(/[^A-Z0-9]/g, ""))
    );
    const donde = firme >= 0 ? firme : pareceRotuloDomicilio(tokens[0] ?? "") ? 0 : -1;
    if (donde < 0) continue;
    inicio = i;
    primeraLinea = tokens.slice(donde + 1).join(" ");
    break;
  }
  if (inicio < 0) return null;

  // El OCR no respeta los saltos del documento: el rótulo siguiente puede venir
  // pegado al final de la misma línea, así que el corte va por texto, no por línea.
  // "LUGAR" entra al patrón porque el OCR parte el rótulo ("LUGAR ATG
  // NACIMIENTO") y sin él la cola se cuela entera en el domicilio.
  const SIGUIENTE = /LUGAR|NACIMIENTO|CU[I1lL]{1,2}[LT]?\s*:|FIRMA|HUELLA|Gabinete/i;
  const hastaElCorte = (linea: string) => linea.split(SIGUIENTE)[0];

  const partes: string[] = [hastaElCorte(primeraLinea)];
  for (let i = inicio + 1; i < lineas.length; i++) {
    const corta = SIGUIENTE.test(lineas[i]);
    partes.push(hastaElCorte(lineas[i]));
    if (corta) break;
  }

  const limpio = partes
    .join(" ")
    .split(/\s+/)
    .filter((t) => {
      const soloLetras = t.replace(/[^A-Za-zÁÉÍÓÚÑáéíóúñ]/g, "");
      if (/^\d+$/.test(t)) return true;                       // alturas
      if (t === "-") return true;                              // separadores del propio DNI
      if (soloLetras.length === 0) return false;               // símbolos sueltos
      // Las partículas cortas son parte de los nombres de lugar ("LA PLATA",
      // "MAR DEL PLATA", "CIUDAD DE ..."): tirarlas por cortas partía el
      // nombre y después no había con qué reconocerlo en el catálogo.
      if (PARTICULAS.has(soloLetras.toUpperCase())) return true;
      if (soloLetras.length <= 2) return false;                // "fs", "OF", "gE"
      return /[AEIOUÁÉÍÓÚ]/i.test(soloLetras);                 // sin vocales = ruido
    })
    .join(" ")
    .replace(/\s+/g, " ")
    .replace(/\s*-\s*$/, "")
    .trim();
  return limpio.length >= 4 ? limpio : null;
}

/** Una fila de un catálogo de referencia tal como la manda el server. */
export interface OpcionRef {
  id: string;
  displayId: string;
  /** Para las provincias, el país al que pertenecen. */
  parentId?: string;
}

export interface DomicilioResuelto {
  streetAndNumber?: string;
  city?: string;
  /** id de la fila del catálogo, que es lo que el formulario guarda. */
  province?: string;
  country?: string;
}

/** Sin acentos, en mayúsculas y sin nada que no sea letra. */
function soloLetrasMayus(s: string): string {
  return String(s ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/[^A-Z]+/g, "");
}

/** Las palabras que identifican un nombre, sin las partículas que lo pegan. */
function tokensSignificativos(s: string): string[] {
  return String(s ?? "")
    .split(/\s+/)
    .map(soloLetrasMayus)
    .filter((t) => t.length > 0 && !PARTICULAS.has(t));
}

/**
 * Cuántas palabras del `nombre` caen en la ventana que arranca en `desde`, y
 * en qué palabra empieza la primera de ellas.
 *
 * La ventana es un poco más ancha que el nombre porque el documento intercala
 * palabras que el catálogo no tiene ("PROVINCIA DE", partículas, basura del
 * OCR), y se cuentan palabras SUELTAS y no una secuencia exacta porque los dos
 * lados escriben distinto: el DNI imprime "CIUDAD DE BUENOS AIRES" donde el
 * catálogo dice "Ciudad Autónoma de Buenos Aires".
 */
function coincidencia(
  palabras: string[],
  nombre: string[],
  desde: number
): { cuantas: number; inicio: number } {
  const hasta = Math.min(palabras.length, desde + nombre.length + 2);
  // Una palabra del catálogo cuenta si está DENTRO de alguna de la ventana: el
  // OCR pega palabras ("BUENOS AIRES" sale "BUENOSAIRES") y eso no debería
  // hacer desaparecer el nombre. Las de una o dos letras se exigen enteras,
  // porque el "FE" de "Santa Fe" aparecería dentro de cualquier cosa.
  const esta = (t: string, palabra: string) =>
    t.length >= 3 ? palabra.includes(t) : palabra === t;
  let cuantas = 0;
  let inicio = -1;
  for (const t of nombre) {
    for (let i = desde; i < hasta; i++) {
      if (!esta(t, palabras[i])) continue;
      cuantas++;
      if (inicio < 0 || i < inicio) inicio = i;
      break;
    }
  }
  return { cuantas, inicio };
}

/**
 * La fila del catálogo que mejor cae en el texto, y dónde empieza.
 *
 * Gana la que comparta MÁS palabras con el texto, que es la que quedó
 * identificada con más precisión: entre "Ciudad Autónoma de Buenos Aires" (tres
 * palabras encontradas) y "Buenos Aires" (dos), el domicilio habla de la
 * primera aunque la segunda coincida "entera". Empatadas en cantidad, gana la
 * que haya quedado más completa, y después la que aparezca más tarde: el
 * domicilio termina en la provincia y el país, y una calle puede llamarse igual
 * más atrás.
 *
 * De un nombre de dos o más palabras tienen que aparecer al menos dos. Sin ese
 * piso, "SANTA ROSA" alcanzaría para dar por encontrada a "Santa Fe".
 */
function mejorFila(
  filas: OpcionRef[],
  palabras: string[]
): { fila: OpcionRef; pos: number } | null {
  let mejor:
    | { fila: OpcionRef; pos: number; cuantas: number; proporcion: number }
    | null = null;
  for (const fila of filas) {
    const nombre = tokensSignificativos(fila.displayId);
    if (nombre.length === 0) continue;
    const minimo = Math.min(nombre.length, 2);
    for (let i = palabras.length - 1; i >= 0; i--) {
      const { cuantas, inicio } = coincidencia(palabras, nombre, i);
      if (cuantas < minimo || inicio < 0) continue;
      const proporcion = cuantas / nombre.length;
      const gana =
        !mejor ||
        cuantas > mejor.cuantas ||
        (cuantas === mejor.cuantas &&
          (proporcion > mejor.proporcion ||
            (proporcion === mejor.proporcion && inicio > mejor.pos)));
      if (gana) mejor = { fila, pos: inicio, cuantas, proporcion };
    }
  }
  return mejor ? { fila: mejor.fila, pos: mejor.pos } : null;
}

/**
 * El domicilio del DNI repartido en los campos del formulario.
 *
 * La versión anterior devolvía solo el texto para que la persona lo acomodara,
 * con el argumento de que provincia y país son referencias a tablas y elegir
 * una fila sería adivinar. Pero el formulario público ya recibe esas tablas,
 * así que no hay nada que adivinar: un pedazo del domicilio o es una fila del
 * catálogo o no lo es.
 *
 * El catálogo es además el ANCLA del reparto. El texto viene como
 * `calle - localidad - provincia - país`, pero contar segmentos desde el final
 * se rompe apenas falta uno o el OCR se come un guion. Se ubican primero la
 * provincia y el país —por sus nombres, no por su posición—, y recién entonces
 * se sabe qué es lo que está antes. Si no se ubican, no se reparte nada y
 * queda el texto crudo, que es lo que había hasta ahora.
 */
export function resolverDomicilio(
  domicilioLeido: string,
  catalogos: { Country?: OpcionRef[]; ProvinceState?: OpcionRef[] }
): DomicilioResuelto {
  const paises = catalogos.Country ?? [];
  const provincias = catalogos.ProvinceState ?? [];
  const palabras = String(domicilioLeido ?? "").split(/\s+/).filter(Boolean);
  if (paises.length === 0 || provincias.length === 0 || palabras.length === 0) return {};

  // Los nombres se buscan sobre las palabras significativas de los dos lados
  // —el catálogo dice "Provincia de Prueba" y el OCR pudo perder el "de"—, con
  // un índice para volver al texto original y cortar por donde corresponde.
  const sig: string[] = [];
  const dondeEnTexto: number[] = [];
  palabras.forEach((palabra, i) => {
    const limpia = soloLetrasMayus(palabra);
    if (limpia.length > 0 && !PARTICULAS.has(limpia)) {
      sig.push(limpia);
      dondeEnTexto.push(i);
    }
  });

  // El país: la última fila del catálogo que aparezca en el texto. Última
  // porque el domicilio termina en el país, y algún nombre de calle podría
  // repetirlo antes.
  const elPais = mejorFila(paises, sig);
  if (!elPais || elPais.pos < 1) return {};
  const { fila: pais, pos: paisPos } = elPais;

  // La provincia: la última que aparezca ANTES del país y que sea DE ese país.
  // Emparejarla con otro país armaría un par que la base rechaza.
  const laProvincia = mejorFila(
    provincias.filter((f) => !f.parentId || f.parentId === pais.id),
    sig.slice(0, paisPos)
  );
  if (!laProvincia || laProvincia.pos < 1) return { country: pais.id };
  const { fila: provincia, pos: provPos } = laProvincia;

  // Lo de más atrás, ya sin el rótulo con que el DNI antecede a la provincia.
  const antes = palabras
    .slice(0, dondeEnTexto[provPos])
    .join(" ")
    .replace(/\s*(PROVINCIA|PCIA\.?|PROV\.?)(\s+DEL?)?\s*$/i, "")
    .replace(/\s*-\s*$/, "")
    .trim();
  // Una partícula colgando en la punta de un segmento es lo que quedó de una
  // palabra que el OCR se comió, no un dato: de una localidad de la que solo
  // sobrevivió el "DE" es mejor no completar nada que escribir "DE" en el
  // campo. Adentro del nombre, en cambio, la partícula es parte de él
  // ("LOMAS DE ZAMORA"), así que solo se recortan las puntas.
  const sinPuntasSueltas = (trozo: string) => {
    const p = trozo.split(/\s+/).filter(Boolean);
    while (p.length > 0 && PARTICULAS.has(soloLetrasMayus(p[0]))) p.shift();
    while (p.length > 0 && PARTICULAS.has(soloLetrasMayus(p[p.length - 1]))) p.pop();
    return p.join(" ");
  };
  const segmentos = antes
    .split(/\s*-\s*/)
    .map((trozo) => sinPuntasSueltas(trozo.trim()))
    .filter(Boolean);
  if (segmentos.length === 0) return { country: pais.id, province: provincia.id };

  // Con un solo segmento no hay localidad que separar: todo es la calle.
  const city = segmentos.length >= 2 ? segmentos[segmentos.length - 1] : undefined;
  const calle = (city ? segmentos.slice(0, -1) : segmentos).join(" - ");
  return {
    streetAndNumber: calle || undefined,
    city,
    province: provincia.id,
    country: pais.id,
  };
}

/**
 * Los nombres del MRZ sin el relleno: el '<' que rellena la línea se lee como
 * L/K/X y queda pegado a los nombres ("RODOLFO XMANUEL LK LLLLLLBHG").
 * Se conservan los que parecen nombres —tienen vocales— y se corta en el
 * primer pedazo que no, que es donde empieza el relleno. A un nombre con una
 * consonante confusable adelante se le saca, porque ahí había un separador.
 */
export function limpiarNombresMrz(givenNames: string): string {
  /** Parte un token por una K/L/X que separe dos nombres de verdad. */
  const despegar = (token: string): string[] => {
    for (let i = 1; i < token.length - 1; i++) {
      if (!"KLX".includes(token[i])) continue;
      // Una letra doblada es del nombre, no un separador: el MRZ separa con UN
      // solo '<'. Sin esto, GUILLERMO se parte por su doble L.
      if (token[i - 1] === token[i] || token[i + 1] === token[i]) continue;
      const izq = token.slice(0, i);
      const der = token.slice(i + 1);
      // Solo si de los dos lados queda algo que puede ser un nombre. Sin este
      // piso se romperían CARLOS, MARCELO o GUILLERMO, que llevan esas letras
      // adentro.
      if (izq.length >= 4 && der.length >= 4 && /[AEIOU]/.test(izq) && /[AEIOU]/.test(der)) {
        return [izq, ...despegar(der)];
      }
    }
    return [token];
  };

  const nombres: string[] = [];
  for (const bruto of String(givenNames ?? "").split(/\s+/).filter(Boolean)) {
    // Un pedazo corto sin vocales es un separador que el OCR leyó como letra:
    // se saltea, no corta la lectura —el nombre puede venir después—.
    if (bruto.length <= 2 && !/[AEIOU]/.test(bruto)) continue;
    for (const parte of despegar(bruto)) {
      if (parte.length >= 2 && /[AEIOU]/.test(parte)) nombres.push(parte);
    }
  }
  return nombres.join(" ");
}

const GENERO: Record<string, "male" | "female"> = { M: "male", F: "female" };

/**
 * Lo que el documento dice, con o sin MRZ.
 *
 * Sin MRZ no hay nada probado y no se envía nada — pero eso no borra lo que el
 * resto del dorso dice. El domicilio y el CUIL están impresos y se leen igual;
 * decir "no pudimos leer el dorso" cuando el domicilio salió entero es falso, y
 * manda a sacar otra foto que va a fallar por la misma razón.
 *
 * Sin MRZ, NADA va como verificado: ni siquiera el CUIL, porque lo que lo
 * respalda es poder contrastarlo contra el número del documento.
 */
/**
 * El número de documento de un MRZ que no llegó a armarse.
 *
 * Las tres líneas del TD1 fallan por separado: la del medio es la más frágil
 * —arranca pegada al margen y es toda dígitos— y por ella se descartaban las
 * otras dos. Medido sobre DNI-Argentina-2020-Reverso-01.jpg: la primera línea
 * sale "IDARGB1544670<6", perfectamente reconocible, y se tiraba entera.
 *
 * Se reparan las confusiones de letra por dígito, que son la causa concreta: el
 * "8" inicial sale como "B" y el `[^0-9]` del parser se lo tragaba en silencio,
 * devolviendo siete dígitos en vez de ocho.
 *
 * NO se rescatan los nombres, y es deliberado: el OCR devuelve los '<' de
 * relleno como K, L o S, y de "MARTINEZ<<LAURA<<<<" sale "MARTINEZS" y
 * "KLAURA SLKLKL...". Un apellido mal leído es peor que ninguno, y el frente lo
 * lee bien por geometría, que es de donde tiene que venir.
 *
 * Nada de esto va como verificado: sin el MRZ armado no hay dígitos de control.
 */
export function documentoDelMrzRoto(texto: string): string | null {
  const lineas = String(texto ?? "")
    .split(/\r?\n/)
    .map((l) => l.replace(/\s+/g, "").toUpperCase());

  for (const l of lineas) {
    // La reparación va DESPUÉS del país: "G" se mapea a "6" y tocar el "ARG" lo
    // convierte en "AR6", con lo cual la línea deja de reconocerse.
    const conPais = l.match(/ID[A-Z<]{3}(.*)$/);
    if (!conPais) continue;
    const numerico = conPais[1].replace(/[BOILSZGQ]/g, (c) =>
      ({ B: "8", O: "0", I: "1", L: "1", S: "5", Z: "2", G: "6", Q: "0" }[c] ?? c)
    );
    const m = numerico.match(/^(\d{7,9})/);
    if (m) return m[1].replace(/^0+/, "") || null;
  }
  return null;
}

export function datosDesdeDni(mrz: MrzTd1Data | null, textoDorso: string): DatosExtraidos {
  const verificados: string[] = [];
  const origenes: Record<string, OrigenDeCampo> = {};
  const d: DatosExtraidos = { verificados, origenes };

  if (!mrz) {
    // El MRZ completo no se pudo armar, pero la primera línea sí se lee y con
    // ella sale el número. El apellido y los nombres vienen del frente, que los
    // lee por geometría sin los '<' de relleno que arruinan la línea 3.
    const documento = documentoDelMrzRoto(textoDorso);
    const cuilSuelto = cuilDelDorso(textoDorso, documento ?? undefined);
    if (cuilSuelto) {
      d.taxIdType = "cuitCuil";
      d.taxId = cuilSuelto;
      origenes.taxId = "dorso";
    } else if (documento) {
      d.taxIdType = "du";
      d.taxId = documento;
      origenes.taxId = "dorso";
    }
    const domicilioSuelto = domicilioDelDorso(textoDorso);
    if (domicilioSuelto) {
      d.domicilioLeido = domicilioSuelto;
      origenes.domicilioLeido = "dorso";
    }
    return d;
  }

  if (mrz.surname) {
    d.lastName = mrz.surname;
    verificados.push("lastName");
    origenes.lastName = "mrz";
  }
  const nombres = limpiarNombresMrz(mrz.givenNames);
  if (nombres) {
    d.firstName = nombres;
    verificados.push("firstName");
    origenes.firstName = "mrz";
  }
  if (mrz.birthDate) {
    d.dateOfBirth = mrz.birthDate;
    verificados.push("dateOfBirth");
    origenes.dateOfBirth = "mrz";
  }
  if (mrz.sex && GENERO[mrz.sex]) d.gender = GENERO[mrz.sex];

  // El campo fiscal, por orden de cuánto lo respalda el documento:
  //
  //  1. El CUIL IMPRESO en el dorso, si se leyó y cierra su verificador. Es un
  //     dato del documento, y va como verificado.
  //  2. El CUIT que le CORRESPONDE al número, construido con el prefijo que
  //     surge del sexo. No está impreso en ninguna parte: es una inferencia, y
  //     por eso NO va como verificado aunque el número del que sale sí lo esté.
  //  3. El número pelado, cuando no hay sexo con qué elegir prefijo.
  const cuil = cuilDelDorso(textoDorso, mrz.documentNumber);
  if (cuil) {
    d.taxIdType = "cuitCuil";
    d.taxId = cuil;
    verificados.push("taxId");
    origenes.taxId = "dorso";
  } else if (mrz.documentNumber) {
    const construido = cuitParaDni(mrz.documentNumber, d.gender === "female" ? "female" : d.gender === "male" ? "male" : null);
    if (construido) {
      d.taxIdType = "cuitCuil";
      d.taxId = construido;
      origenes.taxId = "mrz";
    } else {
      d.taxIdType = "du";
      d.taxId = mrz.documentNumber;
      verificados.push("taxId");
      origenes.taxId = "mrz";
    }
  }

  const domicilio = domicilioDelDorso(textoDorso);
  if (domicilio) {
    d.domicilioLeido = domicilio;
    origenes.domicilioLeido = "dorso";
  }

  return d;
}
